// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/PlanejamentoMrp.tsx
//  Planejamento de necessidades de materiais (MRP) — paridade FrmPlanejamentoProducao.
//  Plano de produção → explosão de BOM → necessidade líquida vs. saldo de estoque.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Calculator, Trash2, AlertTriangle, PackagePlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { mrpService, type MrpResultado } from '@/features/operacional/services/mrpService';
import { getApiErrorMessage } from '@/lib/apiClient';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';

interface DemandaRow {
  _key: number;
  produtoId: number;
  quantidade: number;
}

const fmt = (v: number) => (v ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 4 });

export default function PlanejamentoMrp() {
  const navigate = useNavigate();
  const [demandas, setDemandas] = useState<DemandaRow[]>([{ _key: 1, produtoId: 0, quantidade: 1 }]);
  const [nextKey, setNextKey]   = useState(2);
  const [resultado, setResultado] = useState<MrpResultado | null>(null);

  const addRow = () => setNextKey((k) => { setDemandas((p) => [...p, { _key: k, produtoId: 0, quantidade: 1 }]); return k + 1; });
  const removeRow = (key: number) => setDemandas((p) => p.filter((r) => r._key !== key));
  const updRow = (key: number, field: 'produtoId' | 'quantidade', value: number) =>
    setDemandas((p) => p.map((r) => (r._key === key ? { ...r, [field]: value } : r)));

  const calcMutation = useMutation({
    mutationFn: () => {
      const validas = demandas.filter((d) => d.produtoId > 0 && d.quantidade > 0);
      if (validas.length === 0) throw new Error('Adicione ao menos um produto e quantidade');
      return mrpService.calcular(validas.map(({ produtoId, quantidade }) => ({ produtoId, quantidade })));
    },
    onSuccess: (res) => {
      setResultado(res);
      if (res.necessidades.length === 0) toast.info('Nenhuma necessidade de material apurada');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Calculator className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Planejamento de Produção (MRP)</h1>
          <p className="text-sm text-muted-foreground">
            Calcule a necessidade de insumos para um plano de produção, líquida do saldo em estoque.
          </p>
        </div>
      </div>

      {/* Plano de produção */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Plano de Produção</CardTitle>
            <Button size="sm" variant="outline" className="gap-2" onClick={addRow}>
              <Plus className="h-4 w-4" />Adicionar Produto
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[260px]">Produto Acabado</TableHead>
                <TableHead className="text-right">Quantidade</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {demandas.map((d) => (
                <TableRow key={d._key}>
                  <TableCell>
                    <ProdutoSearchCombobox
                      value={d.produtoId || undefined}
                      onChange={(id) => updRow(d._key, 'produtoId', id)}
                      placeholder="Buscar produto..."
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Input type="number" min={0} step="any" className="w-28 text-right ml-auto"
                      value={d.quantidade}
                      onChange={(e) => updRow(d._key, 'quantidade', Number(e.target.value))} />
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => removeRow(d._key)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex justify-end">
            <Button className="gap-2" onClick={() => calcMutation.mutate()} disabled={calcMutation.isPending}>
              <Calculator className="h-4 w-4" />{calcMutation.isPending ? 'Calculando...' : 'Calcular Necessidades'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resultado */}
      {resultado && (
        <>
          {resultado.produtosSemFicha.length > 0 && (
            <Card className="border-yellow-500/40">
              <CardContent className="pt-4 flex items-start gap-2 text-sm">
                <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0" />
                <div>
                  <p className="font-medium text-yellow-700">Produtos sem ficha técnica/composição</p>
                  <p className="text-muted-foreground">
                    Os produtos {resultado.produtosSemFicha.join(', ')} não têm BOM cadastrado e foram
                    ignorados na explosão. Cadastre a ficha técnica para incluí-los.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Necessidade de Materiais</CardTitle>
            </CardHeader>
            <CardContent>
              {resultado.necessidades.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Nenhuma necessidade apurada.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Matéria-prima ID</TableHead>
                      <TableHead className="text-right">Necessidade Bruta</TableHead>
                      <TableHead className="text-right">Saldo Disponível</TableHead>
                      <TableHead className="text-right">Necessidade Líquida</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resultado.necessidades.map((n) => (
                      <TableRow key={n.materiaPrimaId} className={n.necessidadeLiquida > 0 ? 'bg-red-500/5' : undefined}>
                        <TableCell className="font-medium">{n.materiaPrimaId}</TableCell>
                        <TableCell className="text-right">{fmt(n.necessidadeBruta)}</TableCell>
                        <TableCell className="text-right">{fmt(n.saldoDisponivel)}</TableCell>
                        <TableCell className="text-right font-bold">
                          {n.necessidadeLiquida > 0
                            ? <span className="text-red-600">{fmt(n.necessidadeLiquida)}</span>
                            : <span className="text-green-600">0</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          {n.necessidadeLiquida > 0 && (
                            <Button size="sm" variant="outline" className="gap-1"
                              onClick={() => navigate('/compras/requisicoes')}
                              title="Gerar requisição de compra">
                              <PackagePlus className="h-4 w-4" />Comprar
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
